import { useState } from "react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent("");
      setNext("");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-3xl">
      <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Preferences</p>
      <h1 className="text-4xl font-extrabold tracking-tight mb-10">Settings</h1>

      <div className="bg-white border border-zinc-200 rounded-md p-8 mb-6" data-testid="settings-profile">
        <h2 className="font-bold text-lg mb-4">Profile</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Name" value={user?.name} />
          <Field label="Email" value={user?.email} />
          <Field label="Role" value={user?.role} />
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-md p-8" data-testid="settings-password">
        <h2 className="font-bold text-lg mb-4">Change password</h2>
        <form onSubmit={submit} className="space-y-4 max-w-sm">
          <div>
            <label className="text-sm font-semibold text-zinc-900 mb-1.5 block">Current password</label>
            <input
              data-testid="settings-current-password"
              type="password"
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 outline-none focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-zinc-900 mb-1.5 block">New password</label>
            <input
              data-testid="settings-new-password"
              type="password"
              required
              minLength={6}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 outline-none focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950"
            />
          </div>
          <button
            data-testid="settings-save-password"
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 disabled:opacity-50 text-white font-semibold px-5 py-2.5 rounded-md transition-all active:scale-95"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Update password
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      <div className="font-semibold">{value || "—"}</div>
    </div>
  );
}
